import { useState, type FormEvent } from 'react';
import { Building, Check, Plus, Trash2 } from 'lucide-react';
import { formatarReais, ratearPorUnidade, RECADO_SEM_RATEIO } from '@profitlens/calculo';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Campo, CampoInteiro, CampoReais, Texto as CampoTexto } from '../componentes/Campos';
import { useAuth } from '../contextos/AuthContext';
import {
  criteriosPadrao,
  custoFixoVazio,
  removerCustoFixo,
  salvarCriterios,
  salvarCustoFixo,
  useCriterios,
  useCustosFixos,
  type RascunhoCustoFixo,
} from '../servicos/configuracoes';

/**
 * Custos que existem mesmo sem vender nada.
 *
 * Aluguel, salário, contador, software: eles não aparecem em nenhuma venda
 * isolada, e por isso somem da conta. Deixá-los de fora faz cada análise sair
 * mais otimista do que a realidade — é o erro que faz alguém descobrir no fim
 * do ano que o negócio dava prejuízo enquanto todas as análises diziam
 * "viável".
 *
 * A diluição depende de uma ESTIMATIVA de volume, e é o elo mais frágil da
 * conta. Por isso o número de vendas esperadas fica nesta mesma tela, ao lado
 * do resultado que ele produz: quem mexer num vê o outro mudar.
 */
export function AbaCustosFixos() {
  const { custos, carregando } = useCustosFixos();
  const { criterios } = useCriterios();
  const { perfil } = useAuth();
  const [editando, setEditando] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [volume, setVolume] = useState<number | null>(null);
  const [salvandoVolume, setSalvandoVolume] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const volumeEmVigor = volume ?? criterios?.vendasMensaisEsperadas ?? null;
  const rateio = ratearPorUnidade(custos, volumeEmVigor);

  if (carregando) return <Carregando />;

  async function guardarVolume() {
    if (!perfil) return;
    setErro(null);
    setSalvandoVolume(true);
    try {
      const base = criterios
        ? (({ id, criadoEm, criadoPor, atualizadoEm, atualizadoPor, ...resto }) => resto)(criterios)
        : criteriosPadrao();
      await salvarCriterios(
        { ...base, vendasMensaisEsperadas: volumeEmVigor },
        perfil.id,
        criterios !== null,
      );
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não deu para salvar.');
    } finally {
      setSalvandoVolume(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border-l-3 border-est bg-est/10 px-4 py-3 text-sm text-tinta-2">
        <p>
          <strong className="text-tinta">Aqui entra o que você paga mesmo sem vender.</strong>{' '}
          Aluguel, salário, contador, mensalidade de sistema. Eles não aparecem em nenhuma venda
          isolada — e é exatamente por isso que somem da conta e fazem a margem parecer melhor do
          que é.
        </p>
        <p className="mt-1.5">
          O sistema divide o total pelas vendas que você espera no mês e soma esse pedaço ao custo
          de cada unidade.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-md border border-regua-2 bg-superficie p-4">
        <div className="w-56">
          <CampoInteiro
            rotulo="Vendas esperadas por mês"
            valor={volumeEmVigor}
            aoMudar={setVolume}
            sufixo="un"
            dica="É por este número que o custo fixo é dividido."
          />
        </div>
        <button
          type="button"
          onClick={guardarVolume}
          disabled={salvandoVolume}
          className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          {salvandoVolume ? 'Salvando…' : 'Salvar'}
        </button>

        {/*
          O resultado do rateio fica ao lado do número que o gera. Vender metade
          do previsto DOBRA o custo por unidade — quem mexer no volume precisa
          ver isso acontecer, não descobrir depois.
        */}
        <div className="ml-auto text-right">
          {rateio.definido ? (
            <>
              <p className="text-xs text-tinta-3">Cada venda carrega</p>
              <p className="text-lg font-semibold text-marca">
                {formatarReais(rateio.porUnidadeCents)}
              </p>
              <p className="text-[0.65rem] text-tinta-3">
                {formatarReais(rateio.totalMensalCents)} ÷ {rateio.vendasMensaisEsperadas} vendas
              </p>
            </>
          ) : (
            <p className="max-w-xs text-xs text-c-res">{RECADO_SEM_RATEIO[rateio.motivo]}</p>
          )}
        </div>
      </div>

      {erro && <Erro titulo={erro} />}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            setNovo(true);
            setEditando(null);
          }}
          className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
        >
          <Plus className="size-4" aria-hidden />
          Novo custo fixo
        </button>
      </div>

      {novo && (
        <Formulario
          inicial={custoFixoVazio()}
          usuarioId={perfil?.id ?? ''}
          aoConcluir={() => setNovo(false)}
        />
      )}

      {custos.length === 0 && !novo ? (
        <Vazio
          icone={<Building className="size-8" />}
          titulo="Nenhum custo fixo cadastrado"
          descricao="Se você paga aluguel, salário, contador ou mensalidade de sistema, cadastre aqui. Sem isso a análise mostra margem que não existe."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {custos.map((c) => {
            const aberto = editando === c.id;
            return (
              <li key={c.id} className="rounded-md border border-regua-2 bg-superficie">
                <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <span className={`font-medium ${c.ativo ? '' : 'text-tinta-3 line-through'}`}>
                    {c.nome}
                  </span>
                  <span className="num text-tinta-2">{formatarReais(c.valorMensalCents)}</span>
                  <span className="text-xs text-tinta-3">por mês</span>
                  {!c.ativo && (
                    <span className="rounded bg-tinta-3/12 px-1.5 py-0.5 text-[0.65rem] font-semibold text-tinta-3">
                      FORA DA CONTA
                    </span>
                  )}

                  <div className="ml-auto flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setEditando(aberto ? null : c.id)}
                      className="rounded border border-regua-2 px-2.5 py-1 text-xs hover:border-marca hover:text-marca"
                    >
                      {aberto ? 'Fechar' : 'Editar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => removerCustoFixo(c.id)}
                      aria-label={`Remover ${c.nome}`}
                      className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-pend hover:text-pend"
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </button>
                  </div>
                </div>

                {aberto && (
                  <Formulario
                    id={c.id}
                    inicial={c}
                    usuarioId={perfil?.id ?? ''}
                    aoConcluir={() => setEditando(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {custos.length > 0 && (
        <p className="text-xs text-tinta-3">
          <span className="num">{custos.filter((c) => c.ativo).length}</span> de{' '}
          <span className="num">{custos.length}</span> entram na conta.
        </p>
      )}
    </div>
  );
}

function Formulario({
  id,
  inicial,
  usuarioId,
  aoConcluir,
}: {
  id?: string;
  inicial: RascunhoCustoFixo;
  usuarioId: string;
  aoConcluir: () => void;
}) {
  const [d, setD] = useState<RascunhoCustoFixo>(() => ({ ...inicial }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const editar = <K extends keyof RascunhoCustoFixo>(campo: K, valor: RascunhoCustoFixo[K]) =>
    setD((x) => ({ ...x, [campo]: valor }));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!d.nome.trim()) {
      setErro('Dê um nome ao custo — é como ele vai aparecer na lista.');
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      await salvarCustoFixo(id ?? null, d, usuarioId);
      aoConcluir();
    } catch (ex) {
      setErro(ex instanceof Error ? ex.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={salvar} className="flex flex-col gap-3 border-t border-regua px-4 py-4">
      {erro && <Erro titulo={erro} />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <CampoTexto
          rotulo="Nome"
          valor={d.nome}
          aoMudar={(v) => editar('nome', v ?? '')}
          dica="Ex.: Aluguel do galpão"
        />
        <CampoReais
          rotulo="Valor por mês"
          cents={d.valorMensalCents}
          aoMudar={(v) => editar('valorMensalCents', v ?? 0)}
        />
        <Campo rotulo="Entra na conta">
          <label className="flex items-center gap-2 py-1.5 text-sm">
            <input
              type="checkbox"
              checked={d.ativo}
              onChange={(e) => editar('ativo', e.target.checked)}
            />
            <span className="text-tinta-2">Somar ao rateio</span>
          </label>
        </Campo>
      </div>
      <CampoTexto
        rotulo="Observações"
        valor={d.observacoes}
        aoMudar={(v) => editar('observacoes', v)}
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={salvando}
          className="inline-flex items-center gap-1.5 rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          <Check className="size-4" aria-hidden />
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded border border-regua-2 px-4 py-1.5 text-sm hover:border-marca"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
